// IEA Site Survey Photos mode. Pure: state in, HTML out.
// Words: IEA_PHOTO_VIEWER_WORDS.md (Robert 2026-10-16). Mockup: MOCKUP_IEA_PHOTO_MODE.html v2.
// The third button on the Map screen. Each node shows a camera and the number of photos taken.
// Tap a node: its photos in the left panel. Tap a photo: full screen. Read only, plus Take It Now.
// Actions use data-ph so they never collide with data-mp (Map) or data-cl (Collect).

import { esc } from "./training-screens";
import { cardFor, isMccAsset, photoCount, photoDate, photoSlots, rowPhoto, rowRec, rowPhotoKeys, rowPhotoWord, rowsOf, type PhotoSlot, type Site } from "./survey-collect";
import { codeOf, fullId, nodeLabel, nodeSub, shortName, type MapNode } from "./survey-map";
import { gearKindOf } from "./survey-gear";

/** How a photo's picture stands: shown, still coming, or not reachable now. */
export type { PhotoSrc } from "./survey-photo-store";
import type { PhotoSrc } from "./survey-photo-store";

export interface PhotosPanelState {
  siteNumber: string;
  cur: string | null;
  /** A housing row opened from its housing's list: the point code. */
  row: string | null;
  src: (path: string) => PhotoSrc;
  /** The cloud mark while a photo waits to upload. */
  mark?: (path: string) => string;
}

/** One photo the viewer steps through. */
export interface ShownPhoto {
  path: string;
  type: string;
  label: string;
}

/** What the left panel shows: the node's own photos, or one row of a housing. */
export function panelFocus(site: Site, s: Pick<PhotosPanelState, "cur" | "row">): { kind: "none" } | { kind: "note"; n: MapNode } | { kind: "asset"; n: MapNode; assetId: string } | { kind: "row"; housing: string; point: string; fromHousing: boolean } {
  const n = s.cur ? site.tree.nodes.get(s.cur) : undefined;
  if (!n) return { kind: "none" };
  if (n.kind === "room" || n.kind === "load") return { kind: "note", n };
  if (n.kind === "point") return { kind: "row", housing: n.asset, point: n.ref, fromHousing: false };
  if (s.row) return { kind: "row", housing: n.ref, point: s.row, fromHousing: true };
  return { kind: "asset", n, assetId: n.ref };
}

function rowOf(site: Site, housing: string, point: string): { point: string; assetId: string | null } {
  return rowsOf(site, housing).find((r) => r.point === point) ?? { point, assetId: null };
}

/** A bucket's or breaker's photos as tiles: photo 1 first (missing shows Take Photo), then Photo 2 to 4 taken. */
function rowSlots(site: Site, housing: string, row: { point: string; assetId: string | null }): PhotoSlot[] {
  const p = rowPhoto(site, housing, row);
  const r = rowRec(site, housing, row);
  return [p, ...rowPhotoKeys(r).filter((k) => k !== "Row").map((k) => ({ type: k, path: r.photos[k]!, required: false, target: p.target }))];
}

/** Photo 1's tile says what it is: the Door, or the Breaker on a panel. */
const rowLabel = (site: Site, housing: string, type: string) => {
  if (type !== "Row") return type;
  const a = site.reg.byId.get(housing);
  return rowPhotoWord(Boolean(a && gearKindOf(codeOf(a)) === "panel"));
};

/** The taken photos in the panel, in the order the tiles show them. The viewer steps through these. */
export function shownPhotos(site: Site, s: Pick<PhotosPanelState, "cur" | "row">): ShownPhoto[] {
  const f = panelFocus(site, s);
  if (f.kind === "asset") return photoSlots(site, f.assetId).filter((p) => p.path).map((p) => ({ path: p.path, type: p.type, label: p.type }));
  if (f.kind === "row") {
    return rowSlots(site, f.housing, rowOf(site, f.housing, f.point))
      .filter((p) => p.path)
      .map((p) => ({ path: p.path, type: p.type, label: rowLabel(site, f.housing, p.type) }));
  }
  return [];
}

const plural = (n: number) => `${n} photo${n === 1 ? "" : "s"}`;

function head(title: string, sub: string, count: number | null): string {
  return `<div class="cl-hd"><div class="cl-nm"><b>${esc(title)}</b><small>${esc(sub)}</small></div>${count === null ? "" : `<span class="ph-count">${cameraSvg(count > 0)}${count}</span>`}</div>`;
}

function picture(src: PhotoSrc, label: string): string {
  if (src.state === "ok") return `<img src="${esc(src.src)}" alt="${esc(label)} photo" loading="lazy">`;
  if (src.state === "loading") return '<span class="ph-wait">Loading</span>';
  return '<span class="ph-wait">Needs signal<br>Tap to try again</span>';
}

function tile(slot: PhotoSlot, label: string, index: number, s: PhotosPanelState): string {
  if (slot.path) {
    const src = s.src(slot.path);
    // The big view is the same one Collect uses (survey-photo-store.ts): Done, label, Share, Retake, trash.
    const act = src.state === "error" ? `data-ph="retry" data-ph-path="${esc(slot.path)}"` : `data-pv="open" data-pv-t="${esc(slot.target)}" data-pv-type="${esc(slot.type)}" data-ph-i="${index}"`;
    return `<button type="button" class="ph-tile" ${act}><span class="ph-img">${picture(src, label)}${s.mark?.(slot.path) ?? ""}</span><span class="ph-cap">${esc(label)}<small>${esc(photoDate(slot.path))}</small></span></button>`;
  }
  return `<div class="ph-tile need"><span class="ph-img">Not taken</span><span class="ph-cap"><span>${esc(label)} <small>Not taken</small></span><button type="button" class="ph-take" data-ph="shoot" data-ph-t="${esc(slot.target)}" data-ph-type="${esc(slot.type)}">Take Photo</button></span></div>`;
}

/** The left panel in Photos mode. */
export function photosPanel(site: Site, s: PhotosPanelState): string {
  const f = panelFocus(site, s);
  if (f.kind === "none") return '<div class="cl-note">Tap any node on the diagram to see its photos.</div>';
  if (f.kind === "note") {
    const label = nodeLabel(site.reg, f.n, site.links);
    return `${head(label, nodeSub(site.reg, f.n, site.links), null)}<div class="cl-note">${f.n.kind === "room" ? "A room has no photos. Tap an asset in it." : "Loads have no photos in the survey. The bucket or disconnect that feeds it does."}</div>`;
  }
  if (f.kind === "row") {
    const row = rowOf(site, f.housing, f.point);
    const p = rowPhoto(site, f.housing, row);
    const hName = shortName(site.reg.byId.get(f.housing) ?? { assetId: f.housing, location: "" });
    const slots = rowSlots(site, f.housing, row);
    let h = head(`${hName} · ${f.point}`, `${f.housing}-${f.point}`, slots.filter((x) => x.path).length);
    h += '<div class="cl-sec">Photos</div>';
    h += p.notOpened ? '<div class="cl-note">Not opened. No photo needed.</div>' : rowGrid(slots, (t) => rowLabel(site, f.housing, t), s);
    // Collect is the mode switch at the top: no second button for it (Robert 2026-10-20).
    h += `<div class="sv-btns">${f.fromHousing ? `<button class="sv-btn line" type="button" data-ph="row-close">Back to ${esc(hName)}</button>` : `<button class="sv-btn line" type="button" data-ph="housing" data-ph-a="${esc(f.housing)}">Open ${esc(hName)}</button>`}</div>`;
    return h;
  }
  const slots = photoSlots(site, f.assetId);
  let i = 0;
  let h = head(nodeLabel(site.reg, f.n, site.links), `${fullId(s.siteNumber, f.n)} · ${nodeSub(site.reg, f.n, site.links)}`, photoCount(site, f.n));
  const taken = slots.filter((p) => p.path);
  const missing = slots.filter((p) => !p.path && p.required);
  h += `<div class="cl-sec">Photos</div><div class="ph-grid">${taken.map((p) => tile(p, p.type, i++, s)).join("")}${missing.map((p) => tile(p, p.type, -1, s)).join("")}</div>`;
  if (cardFor(site, f.assetId).kind === "housing") {
    const rows = rowsOf(site, f.assetId);
    const isMcc = isMccAsset(site, f.assetId);
    h += `<div class="cl-sec">${isMcc ? "Buckets" : "Breakers"} · ${rows.length}</div>`;
    if (!rows.length) h += '<div class="cl-note">No rows yet. Positions come from the registry or from breakers typed on the map.</div>';
    else
      h += `<div class="ph-rows">${rows
        .map((r) => {
          const p = rowPhoto(site, f.assetId, r);
          const n = rowPhotoKeys(rowRec(site, f.assetId, r)).length;
          const words = p.notOpened ? "Not opened" : p.path ? `Taken ${photoDate(p.path)}` : "Not taken";
          return `<button type="button" class="ph-row${p.path || p.notOpened ? "" : " need"}" data-ph="row" data-ph-v="${esc(r.point)}"><b>${esc(r.point)}</b><span>${esc(words)}</span><span class="ph-count sm">${cameraSvg(n > 0)}${n}</span></button>`;
        })
        .join("")}</div>`;
  }
  return h;
}

/** The small camera drawn in HTML badges. */
export function cameraSvg(on: boolean): string {
  const c = on ? "#111" : "#8a8f96";
  return `<svg width="16" height="13" viewBox="0 0 16 13" aria-hidden="true"><rect x="1" y="3" width="14" height="9" rx="2" fill="none" stroke="${c}" stroke-width="1.6"/><circle cx="8" cy="7.5" r="2.6" fill="none" stroke="${c}" stroke-width="1.5"/><rect x="5" y="0.8" width="6" height="3" rx="1" fill="${c}"/></svg>`;
}

/** Plural words for the strip. */
export const photosWords = plural;

/** One bucket's or breaker's photo, for the gear front in Photos mode (Gear View step 3). */
export function rowPhotoHtml(site: Site, housing: string, point: string, s: Pick<PhotosPanelState, "src" | "mark">): string {
  const row = rowOf(site, housing, point);
  if (rowPhoto(site, housing, row).notOpened) return '<div class="cl-note">Not opened. No photo needed.</div>';
  return rowGrid(rowSlots(site, housing, row), (t) => rowLabel(site, housing, t), { siteNumber: "", cur: null, row: null, ...s });
}

/** Photo 1 always shows (Take Photo when missing); the others only when taken. Indexes match shownPhotos. */
function rowGrid(slots: PhotoSlot[], label: (type: string) => string, s: PhotosPanelState): string {
  let i = 0;
  return `<div class="ph-grid">${slots.map((p) => (p.path || p.type === "Row" ? tile(p, label(p.type), p.path ? i++ : -1, s) : "")).join("")}</div>`;
}

/** How many photos a bucket or breaker has, 0 to 4; null when it was not opened. */
export function rowPhotoCount(site: Site, housing: string, point: string): number | null {
  const row = rowOf(site, housing, point);
  if (rowPhoto(site, housing, row).notOpened) return null;
  return rowPhotoKeys(rowRec(site, housing, row)).length;
}

/** A housing's own photos (Nameplate, Front, Old Label), for the gear front in Photos mode. */
export function assetPhotosHtml(site: Site, assetId: string, s: Pick<PhotosPanelState, "src" | "mark">): string {
  const st: PhotosPanelState = { siteNumber: "", cur: null, row: null, ...s };
  const slots = photoSlots(site, assetId);
  let i = 0;
  const taken = slots.filter((p) => p.path);
  const missing = slots.filter((p) => !p.path && p.required);
  return `<div class="ph-grid">${taken.map((p) => tile(p, p.type, i++, st)).join("")}${missing.map((p) => tile(p, p.type, -1, st)).join("")}</div>`;
}
